import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoAlertCircleOutline, IoDocumentTextOutline } from 'react-icons/io5';
import { MdDeleteForever, MdDragIndicator } from 'react-icons/md';
import { useMutation, useQueryClient } from 'react-query';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Card, Flex, Modal, Tag, Typography } from 'antd';
import { QualityCheckStatus, SideWithAsset } from 'shared-types';

import PreviewImage, { SPreviewImage } from 'components/PreviewImage/PreviewImage';

import useProductsStore from 'hooks/stores/useProductsStore';
import useSessionStore from 'hooks/stores/useSessionStore';

import API from 'services/API';

import { colors } from 'utils/constants';
import {
  clearSidePreviewRequested,
  hasRequestedSidePreview,
  markSidePreviewRequested,
} from 'utils/sidePreviewRequests';

const { Text } = Typography;

// how long a side with no preview yet needs to stay visible before we request its preview
const PREVIEW_VISIBILITY_TRIGGER_MS = 2000;

const SortablePageItem = ({
  side,
  index,
  sidesCount,
  previewFile,
}: {
  side: SideWithAsset;
  index: number;
  sidesCount: number;
  previewFile: string;
}) => {
  const sessionId = useSessionStore((state) => state.sessionId);
  const session = useSessionStore((state) => state.session);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { setSelectedSide, removeSideFromProduct, selectedSideId } = useProductsStore((state) => {
    return {
      setSelectedSide: state.setSelectedSideId,
      removeSideFromProduct: state.removeSideFromProduct,
      selectedSideId: state.selectedSideId,
    };
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get UI sideList configuration from session options (default = visible)
  const uiSideList = session?.options?.ui?.sideList;
  const shouldShowDeleteSide = uiSideList?.buttons?.deleteSide !== false;
  const shouldShowQualityLabels = uiSideList?.showQualityLabels !== false;

  const { mutate: deleteSideMutation } = useMutation(
    'deleteSide',
    () => API.deleteSide(sessionId, side.id),
    {
      onMutate: async () => {
        // cancel any update side mutation
        await queryClient.cancelQueries(`updateSide-${side.id}`);
      },
    },
  );

  const handleDeleteSide = () => {
    removeSideFromProduct(side.productId, side.id);
    deleteSideMutation();
  };

  // Sides whose asset preview was skipped during upload (options.onUpload.generatePreviewForSides)
  // have no previewImageUrl yet. Show a placeholder until the preview is requested (on click, or
  // once the placeholder has been visible for a while), then show the regular loading spinner
  // until the websocket update delivers the real preview.
  const hasPreview = Boolean(side.previewImageUrl && side.previewImageUrl !== '');
  const [previewRequested, setPreviewRequested] = useState(() => hasRequestedSidePreview(side.id));
  const previewColumnRef = useRef<HTMLDivElement>(null);

  const { mutate: generateSidePreviewMutation, isLoading: isGeneratingPreview } = useMutation(
    ['generateSidePreview', side.id],
    () => API.generateSidePreview(sessionId, side.id),
    {
      onError: () => {
        // allow retrying on the next click/visibility trigger
        clearSidePreviewRequested(side.id);
        setPreviewRequested(false);
      },
    },
  );

  // Auto-trigger (visibility): only fires once per side, so staying in view doesn't keep
  // re-requesting.
  const requestPreviewGeneration = useCallback(() => {
    if (hasPreview || isGeneratingPreview || hasRequestedSidePreview(side.id)) return;
    markSidePreviewRequested(side.id);
    setPreviewRequested(true);
    generateSidePreviewMutation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPreview, isGeneratingPreview, side.id]);

  // Manual trigger (click): always allowed while there's no preview yet, even if a previous
  // request was already fired but never resulted in a preview (e.g. it failed server-side while
  // processing) — the loader would otherwise stay stuck forever.
  const retryPreviewGeneration = useCallback(() => {
    if (hasPreview || isGeneratingPreview) return;
    markSidePreviewRequested(side.id);
    setPreviewRequested(true);
    generateSidePreviewMutation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPreview, isGeneratingPreview, side.id]);

  // once the real preview arrives (via websocket), forget the request so a future re-upload
  // of this side (e.g. asset replaced) can trigger on-demand generation again
  useEffect(() => {
    if (hasPreview) clearSidePreviewRequested(side.id);
  }, [hasPreview, side.id]);

  useEffect(() => {
    if (hasPreview || previewRequested) return;
    const node = previewColumnRef.current;
    if (!node) return;

    let visibilityTimer: ReturnType<typeof setTimeout> | null = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          visibilityTimer = setTimeout(requestPreviewGeneration, PREVIEW_VISIBILITY_TRIGGER_MS);
        } else if (visibilityTimer) {
          clearTimeout(visibilityTimer);
          visibilityTimer = null;
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(node);

    return () => {
      if (visibilityTimer) clearTimeout(visibilityTimer);
      observer.disconnect();
    };
  }, [hasPreview, previewRequested, requestPreviewGeneration]);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: side.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '100%',
    marginBottom: '0.3rem',
    backgroundColor: selectedSideId === side.id ? '#DBEAFE' : '#FAFAF9',
    height: 'auto',
    padding: 0,
    overflow: 'hidden',
    borderRadius: '4px',
  };

  const pageNumber = index + 1;

  const hasWarnings = () => {
    if (!shouldShowQualityLabels) return false;

    // Check quality check warnings/errors
    const hasQualityIssues =
      side.qualityCheck && (side.qualityCheck.hasWarnings || side.qualityCheck.hasErrors);

    // Check for overprint (show indicator, but not as error)
    const hasOverprint = side.asset?.metadata?.hasOverprint;

    return hasQualityIssues || hasOverprint;
  };

  const getWarningBadges = () => {
    if (!shouldShowQualityLabels) return [];

    const badges = [];

    // Quality check badges (if available)
    if (side.qualityCheck) {
      const { scaling, resolution } = side.qualityCheck;

      // Scaling badge
      if (scaling.status !== 'perfect') {
        badges.push({
          text: `schaling`,
          color: scaling.status === 'error' ? '#cf1322' : '#faad14',
        });
      }

      // Resolution badge
      if (resolution.status !== 'perfect') {
        badges.push({
          text: `${resolution.actualDpi} DPI`,
          color: resolution.status === 'error' ? '#cf1322' : '#faad14',
        });
      }
    }

    // Overprint badge (always shown in orange if present)
    if (side.asset?.metadata?.hasOverprint) {
      badges.push({
        text: 'overdruk',
        color: '#faad14', // Orange color, not an error
      });
    }

    return badges;
  };

  if (!side.asset) return null;

  return (
    <Card
      onClick={() => {
        setSelectedSide(side.id);
        retryPreviewGeneration();
      }}
      style={style}
      bodyStyle={{ padding: '8px' }}
      className="sortable-card"
    >
      <Flex
        key={side.id}
        justify="flex-start"
        align="flex-start"
        gap={4}
        wrap="wrap"
        className="card-content-wrapper"
      >
        <div
          className="preview-column"
          ref={previewColumnRef}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {/* Voordat een preview is aangevraagd: statische placeholder i.p.v. loading spinner */}
          {!hasPreview && !previewRequested ? (
            <SPreviewImage>
              <IoDocumentTextOutline size={28} color="#B0B0B0" />
            </SPreviewImage>
          ) : (
            <PreviewImage assetId={side.assetId} sideId={side.id} initialUrl={previewFile} />
          )}
          {/* Paginanummer onder de afbeelding op kleine schermen */}
          <Text
            className="page-number-small-screen"
            style={{
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#616161',
              marginTop: '2px',
              display: 'none',
            }}
          >{`${t('Page')} ${pageNumber}`}</Text>
          {/* Quality warnings for small screens */}
          <div
            className="quality-warnings-small-screen"
            style={{ display: 'none', marginTop: '4px', width: '100%' }}
          >
            {hasWarnings() && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
                <IoAlertCircleOutline
                  style={{
                    fontSize: '0.9rem',
                    color: '#cf1322',
                  }}
                  title="Deze pagina heeft waarschuwingen"
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                  {getWarningBadges().map((badge, index) => (
                    <Tag
                      key={index}
                      style={{
                        margin: 0,
                        fontSize: '0.7rem',
                        padding: '1px 4px',
                        lineHeight: '1.2',
                        border: `1px solid ${badge.color}`,
                        backgroundColor: badge.color === '#cf1322' ? '#fff2f0' : '#fffbe6',
                        color: badge.color,
                      }}
                    >
                      {badge.text}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <Flex
          className="info-column"
          justify="space-between"
          align="flex-start"
          style={{
            width: 'calc(100% - 95px)',
            flexGrow: 1,
            minWidth: '120px',
          }}
        >
          <Flex vertical align="flex-start" style={{ width: '100%' }}>
            <Flex align="center" gap={4}>
              <Text
                className="page-number-normal"
                style={{
                  marginLeft: '6px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: '#616161',
                }}
              >{`${t('Page')} ${pageNumber}`}</Text>
              {hasWarnings() && (
                <IoAlertCircleOutline
                  style={{
                    fontSize: '0.9rem',
                    color: '#cf1322',
                  }}
                  title="Deze pagina heeft waarschuwingen"
                />
              )}
            </Flex>
            {/* Quality badges on next line, aligned under "Pagina xx" */}
            <div
              className="quality-warnings-normal"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '2px',
                marginLeft: '6px', // Same as page number text
                marginTop: '2px',
              }}
            >
              {getWarningBadges().map((badge, index) => (
                <Tag
                  key={index}
                  style={{
                    margin: 0,
                    fontSize: '0.7rem',
                    padding: '1px 4px',
                    lineHeight: '1.2',
                    border: `1px solid ${badge.color}`,
                    backgroundColor: badge.color === '#cf1322' ? '#fff2f0' : '#fffbe6',
                    color: badge.color,
                  }}
                >
                  {badge.text}
                </Tag>
              ))}
            </div>
          </Flex>
          <Flex style={{ marginLeft: 'auto' }} align="center" gap={4}>
            {shouldShowDeleteSide && (
              <MdDeleteForever
                size={20}
                style={{ color: '#B91C1C', cursor: 'pointer' }}
                onClick={() => setIsModalOpen(true)}
              />
            )}
            {sidesCount > 1 && (
              <button
                style={{
                  border: 'none',
                  backgroundColor: colors.lightGray,
                  padding: '2px',
                  borderRadius: '4px',
                  textAlign: 'center',
                  boxShadow: '0px 4px 8px -2px rgba(0,0,0,0.1)',
                  cursor: 'grab',
                }}
                ref={setNodeRef}
                {...attributes}
                {...listeners}
              >
                <MdDragIndicator size={18} style={{ color: colors.darkGray }} />
              </button>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Modal
        title="Confirm Delete"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <p>{t('DeletePageConfirmation')}</p>
        <Flex justify="flex-end">
          <Button onClick={() => setIsModalOpen(false)}>{t('Cancel')}</Button>
          <Button
            style={{ marginLeft: '1rem' }}
            onClick={() => {
              setIsModalOpen(false);
              handleDeleteSide();
            }}
            danger
          >
            {t('Delete')}
          </Button>
        </Flex>
      </Modal>
    </Card>
  );
};

// Voorkomt dat elke websocket preview-update (1 per pagina tijdens verwerking van een
// groot bestand) de volledige, mogelijk duizenden items lange, lijst laat re-renderen.
export default memo(SortablePageItem);
